import React from 'react';
import { Grid, Typography, Divider, Alert, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { FormData } from './types';

interface LegalDocumentsStepProps {
  formData: FormData;
  handleSelectChange: (e: any) => void;
  businessTypes: string[];
  renderDocumentUploadField: (
    documentKey: keyof FormData,
    label: string,
    required?: boolean
  ) => React.ReactNode;
}

// Helper function to render fields in two-column rows
const renderTwoColumnFields = (
  fields: { key: keyof FormData; label: string; required?: boolean }[],
  renderDocumentUploadField: (
    documentKey: keyof FormData,
    label: string,
    required?: boolean
  ) => React.ReactNode
) => {
  const rows = [];
  for (let i = 0; i < fields.length; i += 2) {
    rows.push(
      <Grid container spacing={2} key={i}>
        <Grid item xs={12} md={6}>
          {renderDocumentUploadField(fields[i].key, fields[i].label, fields[i].required)}
        </Grid>
        {fields[i + 1] && (
          <Grid item xs={12} md={6}>
            {renderDocumentUploadField(fields[i + 1].key, fields[i + 1].label, fields[i + 1].required)}
          </Grid>
        )}
      </Grid>
    );
  }
  return rows;
};

const LegalDocumentsStep: React.FC<LegalDocumentsStepProps> = ({ 
  formData, 
  handleSelectChange, 
  businessTypes, 
  renderDocumentUploadField 
}) => (
  <Grid container spacing={3}>
    {/* Business Type Selection */}
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Select Business Type
      </Typography>
      <Box sx={{ maxWidth: 400, mb: 3 }}>
        <FormControl fullWidth required>
          <InputLabel>Business Type</InputLabel>
          <Select
            name="businessType"
            value={formData.businessType}
            onChange={handleSelectChange}
            label="Business Type"
          >
            {businessTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Grid>

    {/* Document Upload Section */}
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Upload Required Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Please upload all required documents for {formData.businessType || "your business type"}
      </Typography>
    </Grid>

    {/* Company Documents */}
    {(formData.businessType === 'Private Limited Company' ||
      formData.businessType === 'Public Limited Company' ||
      formData.businessType === 'LLC') && (
      <>
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" gutterBottom>
            Company Documents
          </Typography>
          <Divider />
        </Grid>
        {renderTwoColumnFields(
          [
            { key: 'moaAoa', label: 'MoA & AoA of the entity' },
            { key: 'certificateOfIncorporation', label: 'Certificate of Incorporation' },
            { key: 'pan_entity', label: 'PAN of the entity' },
            { key: 'gstRegistrationCertificate', label: 'GST Registration Certificate' },
            { key: 'listOfShareholders', label: 'List of Shareholders' },
            { key: 'listOfDirectors', label: 'List of Directors' },
            {
              key: 'kycDirectors',
              label: 'KYC of all directors (PAN, Aadhar/Voter-ID/Passport and Utility Bill)',
            },
            {
              key: 'kycUbos',
              label: "KYC of all UBO's (those having 10% or more in shareholding Pattern)",
            },
            { key: 'bankStatementOneYear', label: 'Bank statements of 1 year' },
            { key: 'itrTwoYears', label: 'ITR of 2 years' },
            {
              key: 'declarationNatureOfBusiness',
              label: "Declaration of Nature of Business (on company's letterhead, duly signed and sealed)",
            },
            { key: 'declarationAuthorisedSignatories', label: 'Declaration of Authorised Signatories' },
            { key: 'declarationSourceOfWealth', label: 'Declaration of Source of wealth' },
          ],
          renderDocumentUploadField
        )}
      </>
    )}

    {/* Partnership Documents */}
    {formData.businessType === 'Partnership' && (
      <>
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" gutterBottom>
            Partnership Documents
          </Typography>
          <Divider />
        </Grid>
        {renderTwoColumnFields(
          [
            { key: 'partnershipDeed', label: 'Partnership deed' },
            { key: 'msmeUdyamAayogCertificate', label: 'MSME udyam aayog certificate (if applicable)', required: false },
            { key: 'gstCertificate', label: 'GST certificate' },
            { key: 'registrationCertificate', label: 'Registration certificate' },
            { key: 'itrTwoFinancialYears', label: 'ITR for 2 financial years' },
            { key: 'panOfFirm', label: 'PAN of firm' },
            { key: 'bankStatementOneYearPartnership', label: 'Bank statement for 1 year' },
            { key: 'operationalOfficeAddressProof', label: 'Operational office address proof (lease deed)' },
            { key: 'kycAllPartners', label: 'KYC of all partners' },
            { key: 'declarationsAndUndertakings', label: 'Declarations and undertakings' },
          ],
          renderDocumentUploadField
        )}
      </>
    )}

    {/* Sole Proprietorship Documents */}
    {formData.businessType === 'Sole Proprietorship' && (
      <>
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" gutterBottom>
            Sole Proprietorship Documents
          </Typography>
          <Divider />
        </Grid>
        {renderTwoColumnFields(
          [
            { key: 'pan_entity', label: 'PAN of the proprietor' },
            {
              key: 'gstRegistrationCertificate',
              label: 'GST Registration Certificate (if applicable)',
              required: false,
            },
            { key: 'bankStatementOneYear', label: 'Bank statements of 1 year' },
            { key: 'itrTwoYears', label: 'ITR of 2 years' },
            { key: 'operationalOfficeAddressProof', label: 'Business address proof' },
            { key: 'declarationNatureOfBusiness', label: 'Declaration of Nature of Business' },
          ],
          renderDocumentUploadField
        )}
      </>
    )}

    {/* No Business Type Selected */}
    {!formData.businessType && (
      <Grid item xs={12} md={6}>
        <Alert severity="info">
          Please select a business type in the previous step to see the required documents.
        </Alert>
      </Grid>
    )}
  </Grid>
);

export default LegalDocumentsStep;
